import { isMonday } from 'date-fns';
import {
  createContext,
  useContext,
  ReactNode,
  useMemo,
  useState,
  useEffect,
} from 'react';
import { useLocation } from 'react-router-dom';

import { BaseDropdownOption } from '../../common/Dropdown';
import { getAnalyticsDropdownOptions } from '../../pages/Analytics/constants';

type AnalyticsContextType = {
  selectedPeriod: BaseDropdownOption;
  checkinData: any[];
  setCheckinData: React.Dispatch<React.SetStateAction<any[]>>;
  setSelectedPeriod: React.Dispatch<React.SetStateAction<BaseDropdownOption>>;
};

const AnalyticsContext = createContext<AnalyticsContextType | undefined>(
  undefined,
);

export function AnalyticsProvider({
  children,
}: {
  children: ReactNode;
}): JSX.Element {
  const location = useLocation();
  const href = location.pathname;

  const analyticsDropdownOptions = useMemo(
    () => getAnalyticsDropdownOptions(href),
    [href],
  );

  // Memoize the period selection logic
  const getDefaultPeriod = useMemo(() => {
    return isMonday(new Date())
      ? analyticsDropdownOptions?.[2] || analyticsDropdownOptions?.[0]
      : analyticsDropdownOptions?.[0];
  }, [analyticsDropdownOptions]);

  const [selectedPeriod, setSelectedPeriod] =
    useState<BaseDropdownOption>(getDefaultPeriod);
  const [checkinData, setCheckinData] = useState<any[]>([]);

  // Update period when options change
  useEffect(() => {
    setSelectedPeriod(getDefaultPeriod);
  }, [getDefaultPeriod]);

  const contextValue = useMemo(
    () =>
      ({
        selectedPeriod,
        checkinData,
        setCheckinData,
        setSelectedPeriod,
      } as const), // Use const assertion for better type inference
    [selectedPeriod, checkinData],
  );

  return (
    <AnalyticsContext.Provider value={contextValue}>
      {children}
    </AnalyticsContext.Provider>
  );
}

export const useAnalytics = (): AnalyticsContextType => {
  const context = useContext(AnalyticsContext);
  if (!context) {
    throw new Error('useAnalytics must be used within an AnalyticsProvider');
  }
  return context;
};
